import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/base/BackToTop";
import { reviews, aboutInfo } from "@/mocks/about";

export default function AboutPage() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 韩漫在线",
    description: "韩漫在线是国内领先的韩国漫画在线阅读平台，提供免费高清韩国漫画全集，下拉式流畅阅读体验。了解我们的品牌故事、用户口碑和联系方式。",
    url: typeof window !== "undefined" ? window.location.href : "",
    about: {
      "@type": "Organization",
      name: "韩漫在线",
      description: aboutInfo.description,
      foundingDate: aboutInfo.founded,
      contactPoint: {
        "@type": "ContactPoint",
        email: aboutInfo.contact.email,
        telephone: aboutInfo.contact.phone,
        contactType: "customer service",
        availableLanguage: "Chinese",
      },
      address: {
        "@type": "PostalAddress",
        streetAddress: aboutInfo.contact.address,
        addressCountry: "CN",
      },
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: typeof window !== "undefined" ? window.location.origin + "/" : "" },
        { "@type": "ListItem", position: 2, name: "关于我们" },
      ],
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: aboutInfo.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <nav className="bg-background-100 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3">
            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-900">关于我们</span>
            </div>
          </div>
        </nav>

        <section className="py-12 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于韩漫在线
            </h1>
            <p className="text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              {aboutInfo.description}
            </p>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center p-4 md:p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                    {stat.number}
                  </p>
                  <p className="text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-2">
              我们的使命
            </h2>
            <p className="text-sm text-foreground-600 max-w-2xl mx-auto text-center leading-relaxed mb-10">
              {aboutInfo.mission}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {aboutInfo.values.map((val) => (
                <div key={val.title} className="p-4 md:p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{val.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{val.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-2">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">来看看读者们怎么说</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {reviews.map((review) => (
                <div key={review.id} className="p-4 md:p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-heading font-bold text-sm">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.date} · {review.source}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < review.rating ? "ri-star-fill text-accent-500" : "ri-star-line text-foreground-300"}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-2">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">关于韩漫在线，你可能想知道</p>
            <div className="max-w-3xl mx-auto flex flex-col gap-3">
              {aboutInfo.faqs.map((faq, idx) => (
                <details key={idx} className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
                  <summary className="px-5 py-4 flex items-center justify-between cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">欢迎随时与我们取得联系</p>
            <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              <div className="p-4 md:p-5 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">邮箱</h3>
                <p className="text-xs text-foreground-500">{aboutInfo.contact.email}</p>
              </div>
              <div className="p-4 md:p-5 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">电话</h3>
                <p className="text-xs text-foreground-500">{aboutInfo.contact.phone}</p>
              </div>
              <div className="p-4 md:p-5 rounded-lg bg-background-50 border border-background-200/70 text-center sm:col-span-2 lg:col-span-1">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">地址</h3>
                <p className="text-xs text-foreground-500">{aboutInfo.contact.address}</p>
              </div>
            </div>
            <div className="mt-8 text-center">
              <p className="text-xs text-foreground-400">
                工作时间：{aboutInfo.contact.workHours} | 微信公众号：{aboutInfo.contact.wechat} | 微博：{aboutInfo.contact.weibo}
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}